import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PasswordInput,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  confirmationCodeLabel,
  confirmationCodeRequiredValidationMessage,
  confirmNewPasswordLabel,
  confirmPasswordRequiredValidationMessage,
  emailLabel,
  emailPlaceholder,
  emailRequiredValidationMessage,
  forgotPasswordButtonLabel,
  newPasswordLabel,
  newPasswordPlaceholder,
  passwordResetMismatch,
  passwordRulesValidationMessage,
} from '../../../../../stringsManager';
import { BinaryIcon, MailIcon } from 'lucide-react';
import { passwordValidationRegex } from 'libs/ui-auth-kit/src/lib/constants';

const formSchema = z
  .object({
    email: z.string().min(1, emailRequiredValidationMessage).email(),
    newPassword: z
      .string()
      .regex(
        new RegExp(passwordValidationRegex),
        passwordRulesValidationMessage
      ),
    confirmNewPassword: z
      .string()
      .min(1, confirmPasswordRequiredValidationMessage),
    confirmationCode: z
      .string()
      .min(1, confirmationCodeRequiredValidationMessage),
  })
  .refine(
    ({ newPassword, confirmNewPassword }) => newPassword === confirmNewPassword,
    {
      message: passwordResetMismatch,
      path: ['confirmNewPassword'],
    }
  );

interface ConfirmResetPasswordFormProps {
  onSubmit: (values: z.infer<typeof formSchema>) => void;
  email: string;
  loading: boolean;
  className?: string;
}

export const ConfirmResetPasswordForm = ({
  className,
  loading,
  onSubmit,
  email,
  ...props
}: ConfirmResetPasswordFormProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email,
      newPassword: '',
      confirmNewPassword: '',
      confirmationCode: '',
    },
  });

  return (
    <div className={cn('grid gap-4 text-foreground', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">{emailLabel}</FormLabel>
                <FormControl>
                  <Input
                    disabled
                    placeholder={emailPlaceholder}
                    {...field}
                    endAdornment={<MailIcon />}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            disabled={loading}
            control={form.control}
            name="confirmationCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">
                  {confirmationCodeLabel}
                </FormLabel>
                <FormControl>
                  <Input {...field} endAdornment={<BinaryIcon />} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            disabled={loading}
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">
                  {newPasswordLabel}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={newPasswordPlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            disabled={loading}
            control={form.control}
            name="confirmNewPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">
                  {confirmNewPasswordLabel}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={newPasswordPlaceholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button disabled={loading} type="submit" className="w-full">
            {forgotPasswordButtonLabel}
          </Button>
        </form>
      </Form>
    </div>
  );
};
